import { gql, useQuery } from '@apollo/client'
import { Grid } from '@mui/material'
import { Campaign, DigitalAsset, Query } from '@promoboxx/graphql-gateway-types'
import { useMemo } from 'react'

import DigitalAssetCard from '@src/components/downloadables/DigitalAssetCard'

import { OrganicCampaignDetailsFilters } from './organicCampaignDetailsFilter'

interface OrganicCampaignDetailsAssetsProps {
  campaignId: Campaign['id']
  shareType: string
  filterApi: OrganicCampaignDetailsFilters
}

const OrganicCampaignDetailsAssets: React.FC<
  OrganicCampaignDetailsAssetsProps
> = (props) => {
  const { data } = useQuery<Query>(GET_CAMPAIGN_ASSETS_QUERY, {
    variables: {
      campaignId: props.campaignId,
    },
  })

  const { tiktokAssets, digitalAssets } = useMemo(() => {
    const sorted = [...(data?.getCampaign?.assets || [])]

    sorted.sort((a, b) => {
      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'created') {
        return (
          new Date(a.created_at || '').valueOf() -
          new Date(b.created_at || '').valueOf()
        )
      }

      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'updated') {
        return (
          new Date(a.updated_at || '').valueOf() -
          new Date(b.updated_at || '').valueOf()
        )
      }

      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'name') {
        return (a.digital_asset?.name || '').localeCompare(
          b.digital_asset?.name || '',
        )
      }

      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'format') {
        return (
          a.digital_asset?.file?.format?.localeCompare(
            b.digital_asset?.file?.format || '',
          ) || 0
        )
      }

      return 0
    })

    if (props.filterApi.debouncedFilterInfo.filter.sortDirection === 'desc') {
      sorted.reverse()
    }

    const tiktokAssets: DigitalAsset[] = []
    const digitalAssets: DigitalAsset[] = []

    for (const asset of sorted) {
      if (!asset.enabled || !asset.digital_asset) {
        continue
      }

      const tiktokEligible =
        asset.digital_asset.eligible_share_types?.tiktok || {}

      if (tiktokEligible.photo || tiktokEligible.link || tiktokEligible.video) {
        tiktokAssets.push(asset.digital_asset)
      } else {
        digitalAssets.push(asset.digital_asset)
      }
    }

    return { tiktokAssets, digitalAssets }
  }, [
    data?.getCampaign?.assets,
    props.filterApi.debouncedFilterInfo.filter.sortDirection,
    props.filterApi.debouncedFilterInfo.filter.sortField,
  ])

  const digitalAssetsToRender =
    props.shareType === 'tiktok' ? tiktokAssets : digitalAssets

  return (
    <>
      <Grid container justifyContent="center" spacing={4}>
        {digitalAssetsToRender.map((x) => {
          return (
            <Grid item xs={12} sm={6} md={6} lg={4}>
              <DigitalAssetCard digitalAsset={x} />
            </Grid>
          )
        })}
      </Grid>
    </>
  )
}

const GET_CAMPAIGN_ASSETS_QUERY = gql`
  query getCampaignAssetsOrganicDetails($campaignId: ID!) {
    getCampaign(
      campaignId: $campaignId
      params: { fields: ["assets.digital_asset.eligible_share_types"] }
    ) {
      id

      assets {
        id
        enabled

        created_at
        updated_at

        digital_asset {
          id

          name

          file_url
          file {
            id
            public_id
            bytes
            width
            height
            resource_type
          }

          eligible_share_types {
            tiktok {
              link
              photo
              video
            }
          }
        }
      }
    }
  }
`

export default OrganicCampaignDetailsAssets
